// App-wide state: session, profile, access, client/site list and the selected site.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { levelOf, type Access } from './access';
import { debounce, useLive } from './live';

export type Site = { id: string; name: string; short_code: string; active: boolean; drive_folder_url: string | null };
export type Profile = { id: string; email: string | null; full_name: string | null; org_id?: string | null };

type AppState = {
  ready: boolean;
  session: Session | null;
  profile: Profile | null;
  access: Access | null;
  sites: Site[];
  siteId: string | null;
  site: Site | null;
  setSiteId: (id: string) => void;
  can: (module: string, level?: number) => boolean;
  reload: () => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AppState | null>(null);

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}

const siteKey = (uid: string) => `pmc3.site.${uid}`;

export function AppProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [access, setAccess] = useState<Access | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteIdState] = useState<string | null>(null);

  const load = useCallback(async (s: Session | null) => {
    if (!s) {
      setProfile(null);
      setAccess(null);
      setSites([]);
      setSiteIdState(null);
      return;
    }
    const uid = s.user.id;
    const [acc, prof, st] = await Promise.all([
      supabase.rpc('c3_my_access'),
      supabase.from('profiles').select('id, email, full_name, org_id').eq('id', uid).maybeSingle(),
      supabase.from('sites').select('id, name, short_code, active, drive_folder_url').is('deleted_at', null).order('sort').order('name'),
    ]);
    setAccess((acc.data as Access) ?? null);
    setProfile((prof.data as Profile) ?? null);
    const list = (st.data as Site[]) ?? [];
    setSites(list);
    let saved: string | null = null;
    try {
      saved = localStorage.getItem(siteKey(uid));
    } catch {
      /* private mode */
    }
    const pick = list.find((x) => x.id === saved) ?? list.find((x) => x.active) ?? list[0] ?? null;
    setSiteIdState(pick?.id ?? null);
  }, []);

  useEffect(() => {
    let alive = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!alive) return;
      setSession(data.session);
      await load(data.session);
      if (alive) setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') void load(s);
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, [load]);

  // roles, permissions or sites changed (by anyone) → refresh my access quietly
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const refreshAccess = useMemo(() => debounce(() => void load(sessionRef.current)), [load]);
  useLive(['user_roles', 'role_grants', 'sites'], refreshAccess, { enabled: Boolean(session) });

  const setSiteId = useCallback(
    (id: string) => {
      setSiteIdState(id);
      try {
        if (session) localStorage.setItem(siteKey(session.user.id), id);
      } catch {
        /* ignore */
      }
    },
    [session],
  );

  const value = useMemo<AppState>(
    () => ({
      ready,
      session,
      profile,
      access,
      sites,
      siteId,
      site: sites.find((s) => s.id === siteId) ?? null,
      setSiteId,
      can: (module: string, level = 1) => levelOf(access, module, siteId) >= level,
      reload: () => load(session),
      signOut: async () => {
        await supabase.auth.signOut();
      },
    }),
    [ready, session, profile, access, sites, siteId, setSiteId, load],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
